/* @jsx h */
// @ts-nocheck
import { defineComponent, h, ref, computed, watchEffect } from "vue";
import { api } from "@app/data/api";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import {
  Reservation,
  TableStatus,
  ReservationTag,
  FloorPlan,
} from "@lemmonhq/types";
import { formatDateInTimezone, formatTimeInTimezone } from "@lemmonhq/orders";
import { useAuth } from "@app/vue/providers/Auth";
import { CustomerProfileBadge } from "@app/vue/components/Business/CustomerProfileBadge";
import { TableSelectionPanel } from "../../ReservationTabs/TableSelectionPanel";
import { type TableSelectorTable } from "@app/vue/components/Business/TableSelector";
import {
  categorizeReservation,
  RESERVATION_STATUS_STYLE,
  TONE_CLASSES,
} from "../../../../../utils/reservationStatus";
import { useToast } from "@app/vue/providers/Toast";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { listReservations } from "../../../utils/trpcReservations";
import { EditReservationModal } from "./EditReservationModal";
import { ReservationComments } from "./ReservationComments";
import { ReservationContactInfo } from "../../../components/ReservationContactInfo";
import { useShop } from "@app/vue/providers/index";
import { useQueryClient } from "@tanstack/vue-query";
import { invalidateAllReservationQueries } from "../../ReservationTabs/data/invalidation";

export interface ReservationDetailsProps {
  reservation: Reservation;
  onClose: () => void;
  onUpdate: (updatedReservation: Reservation) => void;
  onReservationCancel?: (cancelledReservation: Reservation) => void;
  /**
   * Tables-view (map) context. When provided, the inline table-search panel is
   * replaced by a read-only list of the assigned tables, and a "Change Tables"
   * footer button delegates table editing to the floor-plan map instead.
   * Omitted everywhere else (reservations list), so that behaviour is unchanged.
   */
  onChangeTables?: (reservation: Reservation) => void;
}

interface TableWithAvailability {
  table: TableStatus;
  reservations: Reservation[];
  isAvailable: boolean;
  hasConflict: boolean;
}

export const ReservationDetails = defineComponent<ReservationDetailsProps>({
  name: "ReservationDetails",
  props: [
    "reservation",
    "onClose",
    "onUpdate",
    "onReservationCancel",
    "onChangeTables",
  ],
  setup(props) {
    const auth = useAuth();
    const shop = useShop();
    const toast = useToast();
    const { t } = useLocalization();
    const queryClient = useQueryClient();
    const invalidateAllReservationQueriesLocal = async () => {
      await invalidateAllReservationQueries(queryClient);
    };
    const restaurantTimeZone = computed(
      () => auth.user.value?.restaurant.timezone || "UTC",
    );
    const showEditModal = ref(false);
    const updatingStatusAction = ref<string | null>(null);

    // Table assignment state
    const showTableAssignment = ref(false);
    const loadingTables = ref(false);
    const assigningTables = ref(false);
    const allTables = ref<TableStatus[]>([]);
    const dayReservations = ref<Reservation[]>([]);
    const selectedTableIds = ref<number[]>([]);
    const tablesError = ref("");
    const floorPlans = ref<FloorPlan[]>([]);

    // Tag state
    const trpc = useTrpc();
    const availableTags = ref<ReservationTag[]>([]);
    const loadingTags = ref(false);
    const savingTags = ref(false);
    const selectedTagIds = ref<string[]>(
      (props.reservation.tags || []).map((t) => t.id),
    );

    // Notes edit state
    const isEditingNotes = ref(false);
    const notesDraft = ref("");
    const savingNotes = ref(false);

    const startEditNotes = () => {
      notesDraft.value = props.reservation.notes || "";
      isEditingNotes.value = true;
    };

    const handleSaveNotes = async () => {
      try {
        savingNotes.value = true;
        const result = await trpc.reservations.updateNotes.mutate({
          reservation_id: props.reservation.id,
          notes: notesDraft.value.trim(),
        });
        props.onUpdate(result as Reservation);
        isEditingNotes.value = false;
      } catch (e) {
        console.error("Failed to update notes:", e);
        toast.showToast(
          e instanceof Error
            ? e.message
            : t("reservations.details.notes_update_failed"),
          { type: "error" },
        );
      } finally {
        savingNotes.value = false;
      }
    };

    const loadAvailableTags = async () => {
      try {
        loadingTags.value = true;
        const result = await trpc.reservationTags.list.query();
        availableTags.value = result;
      } catch (e) {
        console.error("Failed to load tags:", e);
      } finally {
        loadingTags.value = false;
      }
    };

    const handleToggleTag = async (tagId: string) => {
      const current = [...selectedTagIds.value];
      const idx = current.indexOf(tagId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(tagId);
      }
      selectedTagIds.value = current;

      try {
        savingTags.value = true;
        const result = await trpc.reservationTags.assignToReservation.mutate({
          reservation_id: props.reservation.id,
          tag_ids: current,
        });
        props.onUpdate(result as Reservation);
      } catch (e) {
        console.error("Failed to update tags:", e);
        selectedTagIds.value = (props.reservation.tags || []).map((t) => t.id);
        toast.showToast(t("reservations.details.failed_update_tags"), {
          type: "error",
        });
      } finally {
        savingTags.value = false;
      }
    };

    loadAvailableTags();

    const shouldShowTableAssignment = computed(() => {
      return props.reservation.status === "confirmed";
    });

    const tablesWithAvailability = computed<TableWithAvailability[]>(() => {
      if (allTables.value.length === 0) return [];
      const requestedStart = new Date(props.reservation.time);
      const requestedEnd = new Date(
        requestedStart.getTime() + props.reservation.duration_mins * 60000,
      );
      const result = allTables.value.map((table) => {
        const tableReservations = dayReservations.value.filter((reservation) =>
          reservation.table_assignments?.some(
            (assignment) => assignment.table_id === table.id,
          ),
        );
        const hasConflict = tableReservations.some((reservation) => {
          const resStart = new Date(reservation.time);
          const resEnd = new Date(
            resStart.getTime() + reservation.duration_mins * 60000,
          );
          return requestedStart < resEnd && requestedEnd > resStart;
        });
        return {
          table,
          reservations: tableReservations,
          isAvailable: !hasConflict,
          hasConflict,
        };
      });
      result.sort((a, b) => {
        if (a.isAvailable !== b.isAvailable) return a.isAvailable ? -1 : 1;
        return a.table.table.name.localeCompare(b.table.table.name);
      });
      return result;
    });

    const getRoom = (tableId: number) => {
      for (const fp of floorPlans.value) {
        if (fp.plan?.some((t) => t.tableId === tableId))
          return { name: fp.name, rank: fp.rank ?? 999 };
      }
      return { name: undefined, rank: 999 };
    };

    const tableSelectorData = computed<TableSelectorTable[]>(() =>
      tablesWithAvailability.value.map(
        ({ table, hasConflict, reservations }) => ({
          id: table.id,
          name: table.table.name,
          roomName: getRoom(table.id).name,
          roomRank: getRoom(table.id).rank,
          availability: hasConflict ? "busy" : "free",
          reservationsCount: reservations.length,
          disabled: assigningTables.value,
        }),
      ),
    );

    const availableTablesCount = computed(
      () => tablesWithAvailability.value.filter((t) => t.isAvailable).length,
    );

    const isAnyActionInProgress = computed(
      () => updatingStatusAction.value !== null,
    );

    const updateStatus = async (newStatus: string) => {
      try {
        updatingStatusAction.value = newStatus;
        let reservation: Reservation;
        if (newStatus === "seated") {
          // Use reservations_seat so tables are properly opened (party created,
          // is_busy = 1, table_opened shop event emitted). Plain updateStatus
          // only changes the status column and leaves tables idle.
          const tables = props.reservation.table_assignments.map((a) => ({
            table_id: a.table_id,
            user_id: a.user_id ?? undefined,
          }));
          reservation = await trpc.reservations.seat.mutate({
            reservation_id: props.reservation.id,
            tables,
          });
        } else {
          reservation = await trpc.reservations.updateStatus.mutate({
            reservation_id: props.reservation.id,
            status: newStatus as any,
          });
        }
        props.onUpdate(reservation as Reservation);
        await invalidateAllReservationQueriesLocal();
        props.onClose();
      } catch (e) {
        console.error("Failed to update status:", e);
        alert(t("reservations.details.failed_update_status"));
      } finally {
        updatingStatusAction.value = null;
      }
    };

    const handleCancelReservation = async () => {
      if (!confirm(t("reservations.details.confirm_cancel"))) return;
      try {
        updatingStatusAction.value = "cancel";
        const cancelledReservation = await trpc.reservations.cancel.mutate({
          reservation_id: props.reservation.id,
        });
        if (props.onReservationCancel && cancelledReservation) {
          props.onReservationCancel(cancelledReservation);
        } else {
          props.onUpdate(cancelledReservation);
        }
        await invalidateAllReservationQueriesLocal();
        props.onClose();
      } catch (e) {
        console.error("Failed to cancel reservation:", e);
        alert(t("reservations.details.failed_cancel"));
      } finally {
        updatingStatusAction.value = null;
      }
    };

    const loadTablesData = async () => {
      try {
        loadingTables.value = true;
        tablesError.value = "";
        const results = await Promise.all([
          api.getTablesMeta(),
          listReservations(trpc, {
            date: props.reservation.time,
            timezone: restaurantTimeZone.value,
            limit: "200",
          }),
          api.getFloorPlans(),
        ]);
        allTables.value = results[0] || [];
        dayReservations.value =
          (results[1] as { data: Reservation[] }).data || [];
        floorPlans.value = results[2] || [];
      } catch (e) {
        console.error("Failed to load tables data:", e);
        tablesError.value = t("reservations.details.failed_load_tables");
      } finally {
        loadingTables.value = false;
      }
    };

    // Auto-load tables and pre-select assigned ones. Skipped in map (Tables
    // view) context — there tables are shown read-only and edited on the floor.
    if (props.reservation.status === "confirmed" && !props.onChangeTables) {
      showTableAssignment.value = true;
      selectedTableIds.value = (props.reservation.table_assignments || []).map(
        (a) => a.table_id,
      );
      loadTablesData();
    }

    const handleTableToggle = async (newIds: number[]) => {
      selectedTableIds.value = newIds;
      try {
        assigningTables.value = true;
        const reservation = await trpc.reservations.updateTables.mutate({
          reservation_id: props.reservation.id,
          table_ids: newIds,
        });
        props.onUpdate(reservation as Reservation);
        await invalidateAllReservationQueriesLocal();
      } catch (e) {
        console.error("Failed to assign tables:", e);
        toast.showToast(
          e instanceof Error
            ? e.message
            : t("reservations.details.tables_assign_failed"),
          {
            type: "error",
          },
        );
        // Revert on failure
        selectedTableIds.value = (
          props.reservation.table_assignments || []
        ).map((a) => a.table_id);
      } finally {
        assigningTables.value = false;
      }
    };

    // Status color for the header accent
    // Status colour/badge come from the shared taxonomy so this sheet always
    // agrees with the floor and the lists — e.g. "changed" reads as confirmed
    // (success), not its own amber, matching "changed counts as active".
    const statusTone = computed(
      () =>
        RESERVATION_STATUS_STYLE[
          categorizeReservation(props.reservation.status)
        ].tone,
    );

    const statusColor = computed(
      () => TONE_CLASSES[statusTone.value].borderClass,
    );

    const statusLabel = computed(() => {
      const map: Record<string, string> = {
        confirmed: t("reservations.status.confirmed"),
        changed: t("reservations.status.changed"),
        seated: t("reservations.status.seated"),
        "partially-seated": t("reservations.status.partially_seated"),
        completed: t("reservations.status.completed"),
        "no-show": t("reservations.status.no_show"),
        cancelled_by_host: t("reservations.status.cancelled_by_host"),
        cancelled_by_user: t("reservations.status.cancelled_by_user"),
      };
      return map[props.reservation.status] || props.reservation.status;
    });

    const statusBg = computed(() => {
      const tone = TONE_CLASSES[statusTone.value];
      return `${tone.badgeBg} ${tone.badgeText}`;
    });

    return () => {
      const r = props.reservation;

      return (
        <>
          <RightSheet
            isOpen={true}
            onClose={props.onClose}
            title={
              <div class="flex items-center gap-2.5">
                <span class="text-accent font-bold text-base">
                  {new Date(r.time).toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span class="text-accent font-bold text-base">
                  {formatTimeInTimezone(r.time, restaurantTimeZone.value)}
                </span>
                <span
                  class={`px-2.5 py-1 rounded-md text-xs font-semibold leading-5 ${statusBg.value}`}
                >
                  {statusLabel.value}
                </span>
              </div>
            }
            width="480px"
            height="calc(100dvh - 2rem)"
            mobileVariant="full-screen"
          >
            <div class="flex flex-col h-full min-h-0 overflow-hidden">
              {/* Scrollable content */}
              <div class="flex-1 min-h-0 overflow-y-auto">
                <div class="px-2 py-2 space-y-5">
                  {/* Summary section */}
                  <div class="space-y-5 p-2 bg-base-200 border rounded l-box-shadow">
                    {/* bg-base-200 rounded shadow-xl border p-2 */}
                    {/* ── Line 1: Name | Guests + Duration ── */}
                    <div class="flex items-center gap-3">
                      <span class="text-accent font-semibold text-base truncate">
                        {r.party_name || t("reservations.general.guest_label")}
                      </span>
                      <div class="flex items-center gap-3 flex-shrink-0">
                        <span class="flex items-center gap-1 text-sm text-base-content/70">
                          <span class="material-symbols-outlined text-base text-base-content/60">
                            group
                          </span>
                          {r.party_size}
                        </span>
                        <span class="flex items-center gap-1 text-sm text-base-content/60">
                          <span class="material-symbols-outlined text-base text-base-content/50">
                            schedule
                          </span>
                          {r.duration_mins}m
                        </span>
                      </div>
                      {assigningTables.value && (
                        <span class="loading loading-spinner loading-xs text-base-content/30 ml-auto"></span>
                      )}
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm btn-square ml-auto"
                        onClick={() => {
                          showEditModal.value = true;
                        }}
                        disabled={r.status !== "confirmed"}
                        title={
                          r.status === "confirmed"
                            ? t("reservations.details.edit_reservation_title")
                            : t(
                                "reservations.details.edit_reservation_disabled",
                              )
                        }
                      >
                        <span class="material-symbols-outlined text-lg">
                          edit
                        </span>
                      </button>
                    </div>

                    {/* ── Line 2: Email (left) + Phone (right) ── */}
                    <ReservationContactInfo
                      partyEmail={r.party_email}
                      partyPhone={r.party_phone}
                      customerEmail={r.customer_email}
                      customerPhone={r.customer_phone}
                    />

                    {/* ── Customer Profile (reservations + no-shows in one row) ── */}
                    {r.customer_id && (
                      <CustomerProfileBadge
                        customerId={r.customer_id}
                        email={r.customer_email || r.party_email}
                      />
                    )}
                  </div>

                  {/* ── Requested Experiences (from customer request, read-only) ── */}
                  {r.requested_tags && r.requested_tags.length > 0 && (
                    <div>
                      <h4 class="text-xs font-semibold text-base-content/45 uppercase tracking-wider mb-2">
                        {t("reservations.details.requested_experiences")}
                      </h4>
                      <div class="flex flex-wrap gap-1.5">
                        {r.requested_tags.map((tag) => (
                          <span
                            key={tag.id}
                            class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-current/15"
                            style={{
                              backgroundColor: tag.color
                                ? `${tag.color}20`
                                : "oklch(var(--b3))",
                              color: tag.color || undefined,
                            }}
                          >
                            {tag.icon && (
                              <span class="material-symbols-outlined text-sm">
                                {tag.icon}
                              </span>
                            )}
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Divider ── */}
                  <div class="border-t border-base-content/8 hidden" />

                  {/* ── Notes (experiences + guest notes) ── */}
                  {
                    <div class="p-2 bg-base-200 border rounded l-box-shadow">
                      <h4 class="text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                        {t("reservations.details.notes_header")}
                      </h4>

                      {/* Experience tags */}
                      {loadingTags.value && (
                        <div class="flex flex-wrap gap-2 mb-3">
                          {[1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              class="w-16 h-7 rounded-md bg-base-300/30 animate-pulse"
                            />
                          ))}
                        </div>
                      )}
                      {!loadingTags.value && availableTags.value.length > 0 && (
                        <div class={r.notes ? "mb-3" : ""}>
                          <div class="flex flex-wrap gap-2">
                            {availableTags.value.map((tag) => {
                              const isSelected = selectedTagIds.value.includes(
                                tag.id,
                              );
                              return (
                                <span
                                  key={tag.id}
                                  class={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${
                                    isSelected
                                      ? "border-current/15"
                                      : "border-transparent opacity-30"
                                  }`}
                                  style={{
                                    backgroundColor: tag.color
                                      ? `${tag.color}${isSelected ? "25" : "10"}`
                                      : isSelected
                                        ? "oklch(var(--p) / 0.12)"
                                        : "oklch(var(--b3))",
                                    color: tag.color || undefined,
                                  }}
                                >
                                  {tag.icon && (
                                    <span class="material-symbols-outlined text-sm">
                                      {tag.icon}
                                    </span>
                                  )}
                                  {tag.name}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Guest notes (editable) */}
                      {isEditingNotes.value ? (
                        <div class="flex flex-col gap-2">
                          <textarea
                            class="textarea textarea-bordered min-h-20 text-xs"
                            placeholder={t(
                              "reservations.details.notes_placeholder",
                            )}
                            aria-label={t("reservations.details.notes_header")}
                            maxlength={2000}
                            value={notesDraft.value}
                            onInput={(e: any) =>
                              (notesDraft.value = e.target.value)
                            }
                            disabled={savingNotes.value}
                          />
                          <div class="flex justify-end gap-2">
                            <button
                              type="button"
                              class="btn btn-neutral btn-sm min-h-8"
                              onClick={() => (isEditingNotes.value = false)}
                              disabled={savingNotes.value}
                            >
                              {t("general.words.cancel")}
                            </button>
                            <button
                              type="button"
                              class="btn btn-primary btn-sm min-h-8"
                              onClick={handleSaveNotes}
                              disabled={
                                savingNotes.value ||
                                notesDraft.value.trim() === (r.notes || "")
                              }
                            >
                              {savingNotes.value ? (
                                <span class="loading loading-spinner loading-xs" />
                              ) : null}
                              {t("general.words.save")}
                            </button>
                          </div>
                        </div>
                      ) : r.notes ? (
                        <div class="flex items-start gap-2 px-3 py-2 rounded-md bg-teal-500/8 border border-teal-500/15">
                          <span class="material-symbols-outlined text-sm text-teal-400 mt-0.5">
                            sticky_note_2
                          </span>
                          <p class="flex-1 min-w-0 text-xs text-accent leading-relaxed whitespace-pre-wrap">
                            {r.notes}
                          </p>
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs btn-square min-h-8 min-w-8 text-base-content/50 hover:text-primary"
                            onClick={startEditNotes}
                            title={t("reservations.details.edit_notes")}
                            aria-label={t("reservations.details.edit_notes")}
                          >
                            <span class="material-symbols-outlined text-base">
                              edit_square
                            </span>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          class="btn btn-ghost btn-sm min-h-8 gap-1 text-primary"
                          onClick={startEditNotes}
                        >
                          <span class="material-symbols-outlined text-base">
                            add
                          </span>
                          {t("reservations.details.add_notes")}
                        </button>
                      )}
                    </div>
                  }

                  {/* ── Internal Comments ── */}
                  <ReservationComments
                    reservation={r}
                    onUpdate={props.onUpdate}
                  />

                  {/* ── Table Assignment Panel (confirmed only) ──
                      Reservations-list context: inline search-based picker. */}
                  {shouldShowTableAssignment.value && !props.onChangeTables && (
                    <div class="p-2 bg-base-200 border rounded l-box-shadow">
                      <div class="hidden border-t border-base-content/8" />
                      <TableSelectionPanel
                        tables={tableSelectorData.value}
                        selectedIds={selectedTableIds.value}
                        onChange={handleTableToggle}
                        loading={loadingTables.value}
                        error={tablesError.value}
                        onRetry={loadTablesData}
                        availableCount={availableTablesCount.value}
                        totalCount={allTables.value.length}
                        selectionDisabled={assigningTables.value}
                      />
                    </div>
                  )}

                  {/* ── Assigned tables (read-only) — map (Tables view) context.
                      Editing happens on the floor via the "Change Tables" button. */}
                  {shouldShowTableAssignment.value && props.onChangeTables && (
                    <div class="p-2 bg-base-200 border rounded l-box-shadow">
                      <h4 class="text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                        {t("reservations.general.tables")}
                      </h4>
                      {r.table_assignments && r.table_assignments.length > 0 ? (
                        <div class="flex flex-wrap gap-2">
                          {r.table_assignments.map((a) => (
                            <span
                              key={a.id}
                              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-purple-500/15 text-purple-400 border border-purple-500/30"
                            >
                              <span class="material-symbols-outlined text-sm">
                                table_restaurant
                              </span>
                              {a.table_name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span class="text-xs text-base-content/40">
                          {t("reservations.details.no_table_assigned")}
                        </span>
                      )}
                    </div>
                  )}

                  {/* ── Seated: Table + Time Remaining (read-only) ── */}
                  {r.status === "seated" && (
                    <>
                      <div class="border-t border-base-content/8" />
                      <div>
                        <h4 class="text-xs font-semibold text-base-content uppercase tracking-wider mb-3">
                          {t("reservations.details.seated_at")}
                        </h4>
                        {r.table_assignments &&
                        r.table_assignments.length > 0 ? (
                          <div class="flex flex-wrap gap-2">
                            {r.table_assignments.map((a) => (
                              <span
                                key={a.id}
                                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-purple-500/15 text-purple-400 border border-purple-500/30"
                              >
                                <span class="material-symbols-outlined text-sm">
                                  table_restaurant
                                </span>
                                {a.table_name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span class="text-xs text-base-content/30">
                            {t("reservations.details.no_table_assigned")}
                          </span>
                        )}
                      </div>

                      {/* Time remaining */}
                      {(() => {
                        const startTime = new Date(r.time).getTime();
                        const endTime = startTime + r.duration_mins * 60000;
                        const now = Date.now();
                        const remainingMs = endTime - now;
                        const remainingMins = Math.round(remainingMs / 60000);

                        if (remainingMins <= 0) {
                          const overMins = Math.abs(remainingMins);
                          return (
                            <div class="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-error/10 border border-error/20">
                              <span class="material-symbols-outlined text-base text-error">
                                timer_off
                              </span>
                              <span class="text-xs font-medium text-error">
                                {t("reservations.details.overtime_by").replace(
                                  "{0}",
                                  overMins < 60
                                    ? `${overMins} min`
                                    : `${Math.floor(overMins / 60)}h ${overMins % 60}m`,
                                )}
                              </span>
                            </div>
                          );
                        }

                        const isLow = remainingMins <= 15;
                        return (
                          <div
                            class={`flex items-center gap-2 px-3 py-2.5 rounded-lg ${
                              isLow
                                ? "bg-warning/10 border border-warning/20"
                                : "bg-base-content/5 border border-base-content/10"
                            }`}
                          >
                            <span
                              class={`material-symbols-outlined text-base ${
                                isLow ? "text-warning" : "text-base-content/40"
                              }`}
                            >
                              schedule
                            </span>
                            <span
                              class={`text-xs font-medium ${isLow ? "text-warning" : "text-base-content/50"}`}
                            >
                              {t("reservations.details.remaining").replace(
                                "{0}",
                                remainingMins < 60
                                  ? `${remainingMins} min`
                                  : `${Math.floor(remainingMins / 60)}h ${remainingMins % 60}m`,
                              )}
                            </span>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>

              {/* ── Footer Actions ── */}
              <div class="flex-shrink-0 px-5 pt-3.5 pb-[calc(0.875rem+env(safe-area-inset-bottom))] border-t border-base-content/10 bg-base-100 space-y-2">
                {/* Status actions for confirmed */}
                {r.status === "confirmed" && (
                  <>
                    {/* Top row: Cancel + No Show (red) */}
                    <div class="flex gap-2">
                      <button
                        onClick={handleCancelReservation}
                        class="btn btn-sm btn-error flex-1"
                        disabled={isAnyActionInProgress.value}
                      >
                        {updatingStatusAction.value === "cancel" ? (
                          <span class="loading loading-spinner loading-xs"></span>
                        ) : (
                          <span class="material-symbols-outlined text-sm">
                            cancel
                          </span>
                        )}
                        {t("reservations.general.cancel")}
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(t("reservations.details.confirm_no_show"))
                          )
                            updateStatus("no-show");
                        }}
                        class="btn btn-sm btn-error flex-1"
                        disabled={isAnyActionInProgress.value}
                      >
                        {updatingStatusAction.value === "no-show" ? (
                          <span class="loading loading-spinner loading-xs"></span>
                        ) : (
                          <span class="material-symbols-outlined text-sm">
                            person_cancel
                          </span>
                        )}
                        {t("reservations.details.mark_no_show")}
                      </button>
                    </div>
                    {props.onChangeTables ? (
                      /* Map (Tables view) context: tables are edited on the floor. */
                      (r.table_assignments?.length || 0) > 0 ? (
                        <>
                          {/* Change Tables (floor) + Mark Seated */}
                          <button
                            onClick={() => props.onChangeTables!(r)}
                            class="btn btn-sm btn-outline border-base-content/20 w-full"
                            disabled={isAnyActionInProgress.value}
                          >
                            <span class="material-symbols-outlined text-sm">
                              table_restaurant
                            </span>
                            {t("reservations.table_popup.change_tables")}
                          </button>
                          <button
                            onClick={() => updateStatus("seated")}
                            class="btn btn-sm btn-success w-full"
                            disabled={isAnyActionInProgress.value}
                          >
                            {updatingStatusAction.value === "seated" ? (
                              <span class="loading loading-spinner loading-xs"></span>
                            ) : (
                              <span class="material-symbols-outlined text-sm">
                                chair
                              </span>
                            )}
                            {t("reservations.details.mark_seated")}
                          </button>
                        </>
                      ) : (
                        <>
                          {/* No tables yet — can't seat; prompt to assign on the floor. */}
                          <p class="text-xs text-base-content/50 text-center pb-0.5">
                            {t("reservations.table_popup.assign_to_seat_hint")}
                          </p>
                          <button
                            onClick={() => props.onChangeTables!(r)}
                            class="btn btn-sm btn-primary w-full"
                            disabled={isAnyActionInProgress.value}
                          >
                            <span class="material-symbols-outlined text-sm">
                              table_restaurant
                            </span>
                            {t("reservations.table_popup.assign_tables")}
                          </button>
                        </>
                      )
                    ) : (
                      /* Reservations-list context — unchanged: Mark Seated. */
                      <button
                        onClick={() => updateStatus("seated")}
                        class="btn btn-sm btn-success w-full"
                        disabled={isAnyActionInProgress.value}
                      >
                        {updatingStatusAction.value === "seated" ? (
                          <span class="loading loading-spinner loading-xs"></span>
                        ) : (
                          <span class="material-symbols-outlined text-sm">
                            chair
                          </span>
                        )}
                        {t("reservations.details.mark_seated")}
                      </button>
                    )}
                  </>
                )}

                {r.status === "seated" && (
                  <div class="flex gap-2 w-full">
                    {/* Undo an accidental seating — return to Confirmed */}
                    <button
                      onClick={() => updateStatus("confirmed")}
                      class="btn btn-sm btn-ghost flex-1"
                      disabled={isAnyActionInProgress.value}
                    >
                      {updatingStatusAction.value === "confirmed" ? (
                        <span class="loading loading-spinner loading-xs"></span>
                      ) : (
                        <span class="material-symbols-outlined text-sm">
                          undo
                        </span>
                      )}
                      {t("reservations.details.back_to_confirmed")}
                    </button>
                    <button
                      onClick={() => updateStatus("completed")}
                      class="btn btn-sm btn-success flex-1"
                      disabled={isAnyActionInProgress.value}
                    >
                      {updatingStatusAction.value === "completed" ? (
                        <span class="loading loading-spinner loading-xs"></span>
                      ) : (
                        <span class="material-symbols-outlined text-sm">
                          check_circle
                        </span>
                      )}
                      {t("reservations.details.mark_completed")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </RightSheet>
          <EditReservationModal
            isOpen={showEditModal.value}
            reservation={props.reservation}
            onClose={() => {
              showEditModal.value = false;
            }}
            onSuccess={async (updated: Reservation) => {
              props.onUpdate(updated);
              await invalidateAllReservationQueriesLocal();
              props.onClose();
            }}
          />
        </>
      );
    };
  },
});
